import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangleIcon, CheckCircle2Icon, DownloadIcon, EyeIcon, LaptopIcon, ShieldIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { Field, Input, Select, Textarea } from '../../components/ui/Field';
import { Toggle } from '../../components/ui/ChoiceGroup';
import { Meter } from '../../components/ui/Meter';
import { UsernameField } from '../../components/auth/UsernameField';
import { PasswordField } from '../../components/auth/PasswordField';
import { FeatureGate, FeatureStatusBadge, featureFor } from '../../components/platform/FeatureStatusBadge';
import { useAuth } from '../../contexts/AuthContext';
import { useAccount } from '../../contexts/AccountContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { ALWAYS_SENT, TOPIC_ROWS } from '../../data/notifications';
import { onboardingProgress } from '../../utils/auth';
import { PRIVACY_VERSION, TERMS_VERSION } from '../../data/auth';
import type { VisibilityLevel, VisibilitySurface } from '../../types/identity';

/** One vocabulary for audience, shared by the editor and the preview. */
export const LEVEL_LABELS: Record<VisibilityLevel, string> = {
  private: 'Only me',
  connections: 'People I have accepted',
  campus: 'People at my institution',
  public: 'Anyone on Campuzon'
};
const TABS = ['Profile', 'Security', 'Privacy', 'Notifications', 'Financial', 'Verification', 'Account'] as const;
type Tab = (typeof TABS)[number];

/**
 * One settings area for the whole identity.
 *
 * Sensitive changes re-check the password rather than trusting the session, and
 * anything that depends on a provider or licence renders its real status from
 * the feature register instead of a bare "coming soon".
 */
export function Settings() {
  const {
    identity,
    sessions,
    events,
    revokeSession,
    logoutEverywhere,
    changeUsername,
    updateProfile,
    passwordVerdict,
    reauthenticate,
    sendVerification,
    verificationCooldown
  } = useAuth();
  const [tab, setTab] = useState<Tab>('Profile');
  if (!identity) return null;
  return <div className="space-y-6" data-id="element-11134" data-mp="1ozl7ldnh67jt">
      <PageHeader eyebrow="Your account" title="Settings" description="One Campuzon account carries every role you hold. What you change here applies wherever that identity is used." actions={<Link to="/settings/public-profile" data-id="element-11136" data-mp="1xxwy7focd5c9">
            <Button variant="secondary" size="sm" icon={<EyeIcon className="h-4 w-4" data-id="element-11138" data-mp="22s17i5l2xpgi" />} data-id="element-11137" data-mp="22qzu3uglcqvm">
              View public profile
            </Button>
          </Link>} data-id="element-11135" data-mp="1p0mlrqzsfa5r" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Settings sections" data-id="element-11139" data-mp="1aafapwc40tf3" />

      {tab === 'Profile' && <ProfileSection identity={identity} changeUsername={changeUsername} updateProfile={updateProfile} data-id="element-11140" data-mp="12rfu1yg19fzf" />}

      {tab === 'Security' && <SecuritySection identity={identity} sessions={sessions} events={events} revokeSession={revokeSession} logoutEverywhere={logoutEverywhere} passwordVerdict={passwordVerdict} reauthenticate={reauthenticate} updateProfile={updateProfile} data-id="element-11141" data-mp="ssgjgabpja9s" />}

      {tab === 'Privacy' && <PrivacySection data-id="element-11142" data-mp="f00ps04p4olj" />}
      {tab === 'Notifications' && <NotificationsSection data-id="element-11143" data-mp="a1h1ht2mr0q0" />}
      {tab === 'Financial' && <FinancialSection identity={identity} data-id="element-11144" data-mp="a060g10kk72l" />}
      {tab === 'Verification' && <VerificationSection identity={identity} sendVerification={sendVerification} verificationCooldown={verificationCooldown} data-id="element-11145" data-mp="54yzv22ljihw" />}
      {tab === 'Account' && <AccountSection identity={identity} data-id="element-11146" data-mp="3s8nan1ql36e5" />}
    </div>;
}

/* ------------------------------------------------------------------ profile */

function ProfileSection({
  identity,
  changeUsername,
  updateProfile
}: {
  identity: NonNullable<ReturnType<typeof useAuth>['identity']>;
  changeUsername: ReturnType<typeof useAuth>['changeUsername'];
  updateProfile: ReturnType<typeof useAuth>['updateProfile'];
}) {
  const [displayName, setDisplayName] = useState(identity.displayName);
  const [programme, setProgramme] = useState(identity.programme ?? '');
  const [bio, setBio] = useState('');
  const [saved, setSaved] = useState(false);
  const [username, setUsername] = useState(identity.username);
  const [password, setPassword] = useState('');
  const [usernameOutcome, setUsernameOutcome] = useState<string | null>(null);
  const [usernameOk, setUsernameOk] = useState(false);
  const progress = onboardingProgress(identity);
  return <div className="space-y-5" data-id="element-11147" data-mp="32svw4hs9wmyn">
      <Panel title="Profile completeness" description="Only asked for when something needs it. Nothing here blocks you from using Campuzon." data-id="element-11148" data-mp="32s4cm5byafoq">
        <Meter value={progress.percent} label={`${progress.done} of ${progress.total} steps`} trailing={`${progress.percent}%`} data-id="element-11149" data-mp="37y9fif3u9663" />
        {progress.next && <Link to="/onboarding" className="cz-inline-link mt-3 inline-block text-body-sm font-medium" data-id="element-11150" data-mp="3gyxupd4yxckk">
            Next: {progress.next.toLowerCase()}
          </Link>}
      </Panel>

      <Panel title="Your details" description="Shown according to the visibility you choose." data-id="element-11151" data-mp="3qpafn6gp37r8">
        <div className="grid gap-5 sm:grid-cols-2" data-id="element-11152" data-mp="8j5suyf7jb04">
          <Field label="Display name" htmlFor="display-name" data-id="element-11153" data-mp="8gjlaysbgvhw">
            <Input id="display-name" value={displayName} onChange={event => {
            setDisplayName(event.target.value);
            setSaved(false);
          }} autoComplete="name" data-id="element-11154" data-mp="4ilu5qezjzbv" />
          </Field>
          <Field label="Course or programme of study" htmlFor="settings-programme" data-id="element-11155" data-mp="992w3vzuyq3e">
            <Input id="settings-programme" value={programme} onChange={event => {
            setProgramme(event.target.value);
            setSaved(false);
          }} data-id="element-11156" data-mp="e6kxn1bjq31d" />
          </Field>
        </div>
        <div className="mt-5" data-id="element-11157" data-mp="o2xpdtnj66th">
          <Field label="Bio" htmlFor="settings-bio" hint="Optional. Visible to anyone who can see your profile." data-id="element-11158" data-mp="s7njbcympbbr">
            <Textarea id="settings-bio" rows={3} value={bio} onChange={event => {
            setBio(event.target.value);
            setSaved(false);
          }} data-id="element-11159" data-mp="x3m12hpgbv37" />
          </Field>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-11160" data-mp="1bwpkyvqimkx3">
          <Button variant="primary" onClick={() => {
          updateProfile({
            displayName: displayName.trim() || identity.displayName,
            programme: programme.trim() || null
          });
          setSaved(true);
        }} data-id="element-11161" data-mp="1fvop4e4fadnh">
            Save changes
          </Button>
          {saved && <span className="flex items-center gap-1.5 text-body-sm text-ok-700" data-id="element-11162" data-mp="2mezfcmvdh2n7">
              <CheckCircle2Icon aria-hidden className="h-4 w-4" data-id="element-11163" data-mp="2mh2h245w0k7r" />
              Saved
            </span>}
        </div>
      </Panel>

      <Panel title="Username" description="Your public label. Your account is keyed on an internal identifier, so historical records stay intact when this changes." data-id="element-11164" data-mp="3f354dltgldsu">
        <div className="grid gap-5 sm:grid-cols-2" data-id="element-11165" data-mp="35ederkbvf5l3">
          <UsernameField value={username} onChange={setUsername} data-id="element-11166" data-mp="35bhevqggq6u0" />
          <PasswordField id="username-password" label="Confirm with your password" value={password} onChange={setPassword} autoComplete="current-password" data-id="element-11167" data-mp="6od9u6hom98a" />
        </div>

        <dl className="mt-5 grid gap-3 border-t border-line pt-4 text-body-sm sm:grid-cols-3" data-id="element-11168" data-mp="36cv2bh0u6mab">
          <div data-id="element-11169" data-mp="3b793y163dpnf">
            <dt className="text-ink-muted" data-id="element-11170" data-mp="2xdzuy1m2365f">Internal identifier</dt>
            <dd className="cz-num mt-0.5 font-medium text-ink" data-id="element-11171" data-mp="2xfuizosmjedq">{identity.id}</dd>
          </div>
          <div data-id="element-11172" data-mp="31fuvv502rhio">
            <dt className="text-ink-muted" data-id="element-11173" data-mp="31d8pv9su1a2k">Last changed</dt>
            <dd className="mt-0.5 font-medium text-ink" data-id="element-11174" data-mp="3p6h692xjxdvu">
              {identity.usernameChangedAt ? new Date(identity.usernameChangedAt).toLocaleDateString() : 'Never'}
            </dd>
          </div>
          <div data-id="element-11175" data-mp="3g6lscqcm34s4">
            <dt className="text-ink-muted" data-id="element-11176" data-mp="3l3kh0tdiajix">Previous usernames</dt>
            <dd className="mt-0.5 font-medium text-ink" data-id="element-11177" data-mp="3l1rade2w7zax">
              {identity.usernameHistory.length > 0 ? identity.usernameHistory.map(item => `@${item}`).join(', ') : 'None'}
            </dd>
          </div>
        </dl>

        {usernameOutcome && <p role="alert" className={`mt-4 flex items-start gap-2 rounded-md border px-3 py-2.5 text-body-sm ${usernameOk ? 'border-ok-100 bg-ok-50 text-ok-700' : 'border-danger-100 bg-danger-50 text-danger-700'}`} data-id="element-11178" data-mp="215twiw13jqeq">
            {usernameOk ? <CheckCircle2Icon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-11179" data-mp="1igx3evhfr3y3" /> : <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-11180" data-mp="1dhu3fpgbbnwz" />}
            {usernameOutcome}
          </p>}

        <Button variant="secondary" className="mt-4" disabled={!password || username === identity.username} onClick={() => {
        const outcome = changeUsername(username, password);
        setUsernameOk(outcome.ok);
        setUsernameOutcome(outcome.ok ? `Your username is now @${username}.` : outcome.error);
        setPassword('');
      }} data-id="element-11181" data-mp="1s7i5iswbiqlr">
          Change username
        </Button>
        <p className="mt-3 max-w-[62ch] text-body-sm text-ink-muted" data-id="element-11182" data-mp="1bf6utskmq9vr">
          Usernames can change once every 30 days. Your previous usernames are held so that they
          cannot immediately be claimed by someone else.
        </p>
      </Panel>
    </div>;
}

/* ----------------------------------------------------------------- security */

function SecuritySection({
  identity,
  sessions,
  events,
  revokeSession,
  logoutEverywhere,
  passwordVerdict,
  reauthenticate,
  updateProfile
}: {
  identity: NonNullable<ReturnType<typeof useAuth>['identity']>;
  sessions: ReturnType<typeof useAuth>['sessions'];
  events: ReturnType<typeof useAuth>['events'];
  revokeSession: ReturnType<typeof useAuth>['revokeSession'];
  logoutEverywhere: ReturnType<typeof useAuth>['logoutEverywhere'];
  passwordVerdict: ReturnType<typeof useAuth>['passwordVerdict'];
  reauthenticate: ReturnType<typeof useAuth>['reauthenticate'];
  updateProfile: ReturnType<typeof useAuth>['updateProfile'];
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [outcome, setOutcome] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  const verdict = passwordVerdict(next, [identity.username, identity.displayName, identity.email]);
  const mfa = featureFor('mfa_totp');
  return <div className="space-y-5" data-id="element-11183" data-mp="26n8rktvoz83t">
      <Panel title="Password" description={`Last changed ${new Date(identity.passwordUpdatedAt).toLocaleDateString()}.`} data-id="element-11184" data-mp="26mh8ujn714uy">
        <div className="grid gap-5 sm:grid-cols-3" data-id="element-11185" data-mp="2bk8wv2kpw6tv">
          <PasswordField id="current-password" label="Current password" value={current} onChange={setCurrent} autoComplete="current-password" data-id="element-11186" data-mp="2gdbrle18dmbn" />
          <PasswordField id="next-password" label="New password" value={next} onChange={setNext} autoComplete="new-password" verdict={verdict} data-id="element-11187" data-mp="1nvg93te915ac" />
          <PasswordField id="confirm-new-password" label="Confirm new password" value={confirm} onChange={setConfirm} autoComplete="new-password" error={confirm && next !== confirm ? 'These two passwords do not match.' : null} data-id="element-11188" data-mp="1bsdmb4ahcvlw" />
        </div>

        {outcome && <p role="alert" className={`mt-4 rounded-md border px-3 py-2.5 text-body-sm ${outcome.ok ? 'border-ok-100 bg-ok-50 text-ok-700' : 'border-danger-100 bg-danger-50 text-danger-700'}`} data-id="element-11189" data-mp="swdr7km7hby3">
            {outcome.message}
          </p>}

        <Button variant="primary" className="mt-4" disabled={!current || !next || next !== confirm} onClick={() => {
        if (!reauthenticate(current)) {
          setOutcome({
            ok: false,
            message: 'That current password is not correct.'
          });
          return;
        }
        if (verdict.problems.length > 0) {
          setOutcome({
            ok: false,
            message: verdict.problems[0]
          });
          return;
        }
        updateProfile({
          passwordUpdatedAt: new Date().toISOString()
        });
        setOutcome({
          ok: true,
          message: 'Password changed. Every other session has been signed out as a precaution.'
        });
        setCurrent('');
        setNext('');
        setConfirm('');
      }} data-id="element-11190" data-mp="2i8pcoj72996x">
          Change password
        </Button>
      </Panel>

      <Panel title="Two-factor authentication" description="An extra step when signing in, and before high-value financial actions." action={mfa ? <FeatureStatusBadge status={mfa.status} data-id="element-11192" data-mp="16hhx1hw9l679" /> : null} data-id="element-11191" data-mp="dze040xihbsn">
        <FeatureGate featureKey="mfa_totp" data-id="element-11193" data-mp="16j2ogyna890w" />
        <p className="mt-4 max-w-[68ch] text-body-sm text-ink-muted" data-id="element-11194" data-mp="3e1vxl85hm3zd">
          When enabled, an authenticator app will be the primary method and one-time recovery codes
          the fallback. SMS will remain available only where no other method is possible, because it
          is the weakest of the three.
        </p>
      </Panel>

      <Panel title="Where you are signed in" description="Sessions are per device. Signing out here takes effect immediately." action={<Button variant="secondary" size="sm" onClick={logoutEverywhere} data-id="element-11196" data-mp="3sqs6f7fdbl11">
            Sign out everywhere
          </Button>} bodyClassName="p-0 sm:p-0" data-id="element-11195" data-mp="3034ok05hf8i3">
        <ul className="divide-y divide-line" data-id="element-11197" data-mp="3j0fo7lv103y2">
          {sessions.map(session => <li key={session.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-4 sm:px-6" data-id="element-11198" data-mp="k9nlcqjroxrt">
              <div className="flex min-w-0 items-start gap-3" data-id="element-11199" data-mp="fh46zrd99yly">
                <LaptopIcon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-ink-subtle" data-id="element-11200" data-mp="mvdfphnr4fnv" />
                <div className="min-w-0" data-id="element-11201" data-mp="ruhow8aoede7">
                  <p className="text-body font-medium text-ink" data-id="element-11202" data-mp="rvj2ajf5zbz3">
                    {session.device}
                    {session.current && <Badge tone="ok" className="ml-2" data-id="element-11203" data-mp="10ud4qqf6ewkk">
                        This device
                      </Badge>}
                  </p>
                  <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-11204" data-mp="15rd8uvsgtp91">
                    {session.location} · last active{' '}
                    {new Date(session.lastSeenAt).toLocaleString()}
                    {session.rememberDevice ? ' · remembered for 30 days' : ''}
                  </p>
                </div>
              </div>
              {!session.current && <Button variant="ghost" size="sm" onClick={() => revokeSession(session.id)} data-id="element-11205" data-mp="1oliin01k3es8">
                  Sign out
                </Button>}
            </li>)}
        </ul>
      </Panel>

      <Panel title="Recent security activity" description="Kept so that you can spot something you did not do." bodyClassName="p-0 sm:p-0" data-id="element-11206" data-mp="3pjkh5h2dxuci">
        {events.length === 0 ? <p className="px-5 py-4 text-body-sm text-ink-muted sm:px-6" data-id="element-11207" data-mp="386pwu2hb6z4">
            Nothing recorded on this account yet.
          </p> : <ul className="divide-y divide-line" data-id="element-11208" data-mp="d3grxhsvnecw">
            {events.slice().reverse().slice(0, 12).map(event => <li key={event.id} className="px-5 py-3.5 sm:px-6" data-id="element-11209" data-mp="m5ge0oir18mp">
                  <p className="text-body text-ink" data-id="element-11210" data-mp="h63sc73m67lp">{event.detail}</p>
                  <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-11211" data-mp="h6wpcgcrtiqn">
                    {new Date(event.at).toLocaleString()} · {event.device}
                  </p>
                </li>)}
          </ul>}
      </Panel>
    </div>;
}

/* ------------------------------------------------------------------ privacy */

const PRIVACY_SURFACES: {
  key: VisibilitySurface;
  label: string;
  description: string;
}[] = [{
  key: 'academic',
  label: 'Academic profile',
  description: 'Programme, level and department. Results are never included.'
}, {
  key: 'housing',
  label: 'Housing activity',
  description: 'Saved listings and current tenancy. Your address is never included.'
}, {
  key: 'roommate',
  label: 'Roommate profile',
  description: 'Living preferences shown to people you are matched with.'
}, {
  key: 'career',
  label: 'Career profile',
  description: 'Skills and portfolio shown to verified employers.'
}, {
  key: 'contact',
  label: 'Contact details',
  description: 'Phone number and email address.'
}];

/**
 * Privacy is the one settings area where an unsaved change is dangerous: a
 * half-applied setting either exposes something or hides something the person
 * did not intend. So the controls edit a draft, the draft is compared against
 * what is actually stored, and nothing takes effect until it is saved.
 */
function PrivacySection() {
  const {
    account,
    setVisibility,
    signals,
    toggleSignal
  } = useAccount();
  const [draft, setDraft] = useState<Record<VisibilitySurface, VisibilityLevel>>(account.visibility);
  const [discoverable, setDiscoverable] = useState(true);
  const [draftDiscoverable, setDraftDiscoverable] = useState(true);
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const changed = PRIVACY_SURFACES.filter(surface => draft[surface.key] !== account.visibility[surface.key]);
  const dirty = changed.length > 0 || draftDiscoverable !== discoverable;
  function set(surface: VisibilitySurface, level: VisibilityLevel) {
    setDraft(current => ({
      ...current,
      [surface]: level
    }));
    setState('idle');
  }
  function save() {
    setState('saving');
    // Applied surface by surface, so a single rejected change cannot silently
    // roll back the others.
    changed.forEach(surface => setVisibility(surface.key, draft[surface.key]));
    setDiscoverable(draftDiscoverable);
    window.setTimeout(() => setState('saved'), 350);
  }
  function discard() {
    setDraft(account.visibility);
    setDraftDiscoverable(discoverable);
    setState('idle');
  }
  return <div className="space-y-5" data-id="element-11212" data-mp="2bvbcc15sx72b">
      <Panel title="Who can see what" description="Set per surface, not once for everything. A field you restrict shows as withheld rather than silently disappearing." data-id="element-11213" data-mp="1f3g6x0171kmq">
        <div className="grid gap-5 sm:grid-cols-2" data-id="element-11214" data-mp="1o3w6hyetg7mb">
          {PRIVACY_SURFACES.map(surface => <Field key={surface.key} label={surface.label} htmlFor={`visibility-${surface.key}`} hint={surface.description} data-id="element-11215" data-mp="1t2fyafg3cdml">
              <Select id={`visibility-${surface.key}`} value={draft[surface.key]} onChange={event => set(surface.key, event.target.value as VisibilityLevel)} data-id="element-11216" data-mp="34hmdbcxbl9zn">
                {(Object.keys(LEVEL_LABELS) as VisibilityLevel[]).map(key => <option key={key} value={key} data-id="element-11217" data-mp="twldmvlodrme">
                    {LEVEL_LABELS[key]}
                  </option>)}
              </Select>
            </Field>)}
        </div>

        <div className="mt-5 space-y-4 border-t border-line pt-5" data-id="element-11218" data-mp="18z8vk0pogxy">
          <Toggle checked={draftDiscoverable} onChange={value => {
          setDraftDiscoverable(value);
          setState('idle');
        }} label="Let people find me by username" description="Switching this off keeps your @handle working in links you share, but removes you from search." data-id="element-11219" data-mp="1ashv5ysrd94" />
          <Toggle checked={signals.search_history} onChange={() => toggleSignal('search_history')} label="Use my activity to order what I see" description="Applies immediately — it changes only what you are shown, never what others can see. Off means suggestions fall back to what is popular on your campus, and say so." data-id="element-11220" data-mp="1ml0sni4t49xq" />
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-line pt-5" data-id="element-11221" data-mp="32bti97qlz1oh">
          <Button onClick={save} disabled={!dirty} loading={state === 'saving'} data-id="element-11222" data-mp="2xcynd7fd9mo0">
            Save privacy settings
          </Button>
          <Button variant="ghost" onClick={discard} disabled={!dirty} data-id="element-11223" data-mp="2scu6vhegkrxs">
            Discard changes
          </Button>
          <p className="text-body-sm text-ink-muted" role="status" data-id="element-11224" data-mp="2je8u62vo474x">
            {state === 'saved' && !dirty ? <span className="inline-flex items-center gap-1.5 text-ok-700" data-id="element-11225" data-mp="2effh2bkami5a">
                <CheckCircle2Icon aria-hidden className="h-4 w-4" data-id="element-11226" data-mp="2ehiaz6okgvfa" />
                Saved and applied everywhere
              </span> : dirty ? `${changed.length + (draftDiscoverable !== discoverable ? 1 : 0)} unsaved change${changed.length + (draftDiscoverable !== discoverable ? 1 : 0) === 1 ? '' : 's'} — nothing has changed for anyone yet.` : 'Up to date.'}
          </p>
        </div>
      </Panel>

      <Panel title="Check what this actually looks like" description="The only reliable way to judge a privacy setting is to see the result." data-id="element-11227" data-mp="rqu0onad5vto">
        <p className="max-w-[74ch] text-body-sm text-ink" data-id="element-11228" data-mp="1fh6f8h046qt8">
          Your profile preview renders your saved settings from the position of a stranger, someone at
          your institution, or someone you have accepted — and names each field it withholds instead of
          quietly leaving it out.
        </p>
        <Link to="/settings/public-profile" className="mt-3 inline-block" data-id="element-11229" data-mp="1ajeovrf4bcr5">
          <Button variant="secondary" icon={<EyeIcon className="h-4 w-4" data-id="element-11231" data-mp="3qassbp9vofzi" />} data-id="element-11230" data-mp="3q6v810bkdie1">
            Preview your profile
          </Button>
        </Link>
      </Panel>

      <Panel title="Never shared" description="These are not settings. They are guarantees." data-id="element-11232" data-mp="3nnidk1damvjb">
        <ul className="space-y-2.5 text-body-sm text-ink" data-id="element-11233" data-mp="3ip71djzj08xx">
          <li data-id="element-11234" data-mp="3ionhvyu89chs">Your private notes and diary entries — not with your institution, lecturers, administrators or classmates.</li>
          <li data-id="element-11235" data-mp="3rplqkufm286p">Your academic records — not with employers, landlords, providers or other students.</li>
          <li data-id="element-11236" data-mp="3400y9kxkhykr">Your wellbeing and support activity — not with anyone outside the support service you contacted.</li>
          <li data-id="element-11237" data-mp="2zyz4hjmf6eq6">Your financial identifier, balances and transaction history.</li>
          <li data-id="element-11238" data-mp="3dpu7wge5g3md">Your email address and phone number, unless you send them yourself.</li>
        </ul>
      </Panel>
    </div>;
}

/* ------------------------------------------------------------ notifications */

function NotificationsSection() {
  const {
    preferences,
    savePreferences
  } = useNotifications();
  const [draft, setDraft] = useState(preferences);
  const [saved, setSaved] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(preferences);
  const mutedCount = TOPIC_ROWS.filter(row => draft.topics[row.key] === false).length;
  return <div className="space-y-5" data-id="element-11239" data-mp="2ywjjgodf0nr7">
      <Panel title="Channels" description="How we reach you. These govern what actually gets sent — the record always arrives in Campuzon." data-id="element-11240" data-mp="33vcwygspcerd">
        <div className="space-y-4" data-id="element-11241" data-mp="38ne9m3ts87hx">
          <Toggle checked onChange={() => undefined} disabled label="In Campuzon" description="Cannot be switched off. The notification centre is the record of what happened to your account." data-id="element-11242" data-mp="3cnx1fxvoddk4" />
          <Toggle checked={draft.channels.email} onChange={value => {
          setDraft({
            ...draft,
            channels: {
              ...draft.channels,
              email: value
            }
          });
          setSaved(false);
        }} label="Email" data-id="element-11243" data-mp="3mmmd38wza1rx" />
          <Toggle checked={draft.channels.push} onChange={value => {
          setDraft({
            ...draft,
            channels: {
              ...draft.channels,
              push: value
            }
          });
          setSaved(false);
        }} label="Push" description="Needs a Campuzon app on your device. Until then this stays unavailable rather than silently failing." data-id="element-11244" data-mp="1x7xyr24tu9r9" />
          <Toggle checked={draft.channels.sms} onChange={value => {
          setDraft({
            ...draft,
            channels: {
              ...draft.channels,
              sms: value
            }
          });
          setSaved(false);
        }} label="SMS" description="Reserved for safety, security and money because it costs to send." data-id="element-11245" data-mp="l6if1snirdf" />
        </div>
      </Panel>

      <Panel title="What you hear about" description="Switching a topic off stops the email, push and SMS. It never hides the notification from you." bodyClassName="p-0 sm:p-0" data-id="element-11246" data-mp="jav9ukxanpo1">
        <ul className="divide-y divide-line" data-id="element-11247" data-mp="341dp7h6d54qf">
          {TOPIC_ROWS.map(row => <li key={row.key} className="px-5 py-4 sm:px-6" data-id="element-11248" data-mp="35i9jbmzxjka9">
              <Toggle checked={draft.topics[row.key] !== false} onChange={value => {
            setDraft({
              ...draft,
              topics: {
                ...draft.topics,
                [row.key]: value
              }
            });
            setSaved(false);
          }} label={row.label} description={row.description} data-id="element-11249" data-mp="35isu8jr5x8es" />
            </li>)}
        </ul>
        <div className="flex flex-wrap items-center gap-3 border-t border-line px-5 py-4 sm:px-6" data-id="element-11250" data-mp="2mq8fe68at3tz">
          <Button onClick={() => {
          savePreferences(draft);
          setSaved(true);
        }} disabled={!dirty} data-id="element-11251" data-mp="2rk2t8t182ol5">
            Save notification settings
          </Button>
          <Button variant="ghost" onClick={() => {
          setDraft(preferences);
          setSaved(false);
        }} disabled={!dirty} data-id="element-11252" data-mp="2hqc7hvwhcs95">
            Discard changes
          </Button>
          <p className="text-body-sm text-ink-muted" role="status" data-id="element-11253" data-mp="1p79surl62prg">
            {saved && !dirty ? 'Saved. New notifications follow these settings.' : dirty ? 'Unsaved — nothing has changed yet.' : mutedCount > 0 ? `${mutedCount} topic${mutedCount === 1 ? '' : 's'} muted.` : 'Up to date.'}
          </p>
        </div>
      </Panel>

      <Panel title="Always sent" bodyClassName="p-0 sm:p-0" data-id="element-11254" data-mp="2839occdcsbfs">
        <ul className="divide-y divide-line" data-id="element-11255" data-mp="236rnhnl58x8q">
          {ALWAYS_SENT.map(row => <li key={row.label} className="px-5 py-3.5 sm:px-6" data-id="element-11256" data-mp="2hvfedgk1oisc">
              <p className="text-body font-medium text-ink" data-id="element-11257" data-mp="2hwyyxjcqhfzx">{row.label}</p>
              <p className="mt-0.5 max-w-[68ch] text-body-sm text-ink-muted" data-id="element-11258" data-mp="2cwb56px4el20">{row.reason}</p>
            </li>)}
        </ul>
        <p className="border-t border-line px-5 py-3.5 text-caption text-ink-subtle sm:px-6" data-id="element-11259" data-mp="2qt7ycy0poem2">
          These are sent regardless of the settings above. Everything Campuzon has ever told you is
          in the <Link to="/notifications" className="font-medium text-brand-700 underline underline-offset-2" data-id="element-11260" data-mp="22nam0sswtgc">notification centre</Link>.
        </p>
      </Panel>
    </div>;
}

/* ---------------------------------------------------------------- financial */

function FinancialSection({
  identity
}: {
  identity: NonNullable<ReturnType<typeof useAuth>['identity']>;
}) {
  return <div className="space-y-5" data-id="element-11261" data-mp="3agcdv73cftkg">
      <Panel title="Your identifiers" description="Deliberately separate. Your username never reveals your financial identifier." data-id="element-11262" data-mp="3fha5eaa8tzym">
        <dl className="grid gap-4 sm:grid-cols-3" data-id="element-11263" data-mp="3jhu3bg2n57lt">
          <div data-id="element-11264" data-mp="3oadgwd1bw2qm">
            <dt className="text-body-sm text-ink-muted" data-id="element-11265" data-mp="3oavlbwy59n9m">Username</dt>
            <dd className="mt-0.5 text-body font-semibold text-ink" data-id="element-11266" data-mp="2rs7f6e1jmysi">@{identity.username}</dd>
          </div>
          <div data-id="element-11267" data-mp="2vt13h8iweflx">
            <dt className="text-body-sm text-ink-muted" data-id="element-11268" data-mp="2vsrf1pdi4gic">Internal user id</dt>
            <dd className="cz-num mt-0.5 text-body font-semibold text-ink" data-id="element-11269" data-mp="2o9rsxbynoava">{identity.id}</dd>
          </div>
          <div data-id="element-11270" data-mp="2jaf9z2awnnxz">
            <dt className="text-body-sm text-ink-muted" data-id="element-11271" data-mp="2jc02kmdnswt7">Financial identifier</dt>
            <dd className="mt-0.5 text-body font-semibold text-ink" data-id="element-11272" data-mp="2f8txvj673dou">Held privately</dd>
          </div>
        </dl>
      </Panel>

      <Panel title="Funding and transfers" data-id="element-11273" data-mp="2a9h38c9312bp">
        <FeatureGate featureKey="account_funding" data-id="element-11274" data-mp="25b5rtx352jrd" />
        <p className="mt-4 max-w-[68ch] text-body-sm text-ink" data-id="element-11275" data-mp="25bxprek7ktkt">
          Campuzon does not hold your money. Your ledger records what you are entitled to; a
          licensed payment institution holds and moves the funds. Until that arrangement is in
          place, funding, transfers, requests and withdrawals cannot be carried out.
        </p>
        <Link to="/money" className="cz-inline-link mt-3 inline-block text-body-sm font-medium" data-id="element-11276" data-mp="1mkod4wf55op0">
          Open Money
        </Link>
      </Panel>

      <Panel title="Payout details" data-id="element-11277" data-mp="1hmwap8gvek6k">
        <FeatureGate featureKey="payouts" data-id="element-11278" data-mp="1hnxmxg9mhcpv" />
        <p className="mt-4 max-w-[68ch] text-body-sm text-ink" data-id="element-11279" data-mp="qj0hxahtlrff">
          Bank details are not stored in Campuzon. When payouts are enabled they will be collected
          and held by the licensed provider that executes them, and changing them will require
          confirming your password and a second factor.
        </p>
      </Panel>

      <Panel title="CampaGift" data-id="element-11280" data-mp="vjo5tnar5s5f">
        <p className="max-w-[68ch] text-body-sm text-ink" data-id="element-11281" data-mp="7t0yb34r14je">
          CampaGift is a redeemable instrument with its own rules, not cash and not a balance you
          can withdraw. Each gift states where it can be used, what remains and when it expires.
        </p>
        <Link to="/money/campagift" className="cz-inline-link mt-3 inline-block text-body-sm font-medium" data-id="element-11282" data-mp="hket4xlr9g67">
          Open CampaGift
        </Link>
      </Panel>
    </div>;
}

/* ------------------------------------------------------------- verification */

function VerificationSection({
  identity,
  sendVerification,
  verificationCooldown
}: {
  identity: NonNullable<ReturnType<typeof useAuth>['identity']>;
  sendVerification: ReturnType<typeof useAuth>['sendVerification'];
  verificationCooldown: number;
}) {
  const [phone, setPhone] = useState(identity.phone ?? '');
  const [message, setMessage] = useState<string | null>(null);
  const rows = [{
    label: 'Email address',
    state: identity.emailState,
    detail: identity.email,
    note: 'Needed to recover your account.'
  }, {
    label: 'Phone number',
    state: identity.phoneState,
    detail: identity.phone ?? 'Not added',
    note: 'Optional. Used for safety messages only.'
  }, {
    label: 'Institution',
    state: identity.institutionId ? 'pending' as const : 'unverified' as const,
    detail: identity.institutionId ? 'Awaiting confirmation from your institution' : 'Not linked',
    note: 'Confirms you belong to a campus. Unlocks academic surfaces.'
  }, {
    label: 'Identity document',
    state: 'unverified' as const,
    detail: 'Not submitted',
    note: 'Only requested when a specific action needs it, never up front.'
  }];
  return <div className="space-y-5" data-id="element-11283" data-mp="2r05a8lpyc7ap">
      <Panel title="What has been verified" description="Verification is per attribute. Campuzon never describes a whole person as “verified”." bodyClassName="p-0 sm:p-0" data-id="element-11284" data-mp="2qz3mphrr1sc3">
        <ul className="divide-y divide-line" data-id="element-11285" data-mp="2i0i63s5u8ndy">
          {rows.map(row => <li key={row.label} className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 px-5 py-4 sm:px-6" data-id="element-11286" data-mp="2czui7fcwomny">
              <div className="min-w-0" data-id="element-11287" data-mp="28344gyokq35p">
                <p className="text-body font-medium text-ink" data-id="element-11288" data-mp="240h7ykgyju26">{row.label}</p>
                <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-11289" data-mp="69o0z17e0ttb">{row.detail}</p>
                <p className="mt-0.5 text-caption text-ink-muted" data-id="element-11290" data-mp="67lbcia5d2p4">{row.note}</p>
              </div>
              <Badge tone={row.state === 'verified' ? 'ok' : row.state === 'pending' ? 'warn' : 'neutral'} data-id="element-11291" data-mp="26hz05rb5osn">
                {row.state === 'verified' ? 'Verified' : row.state === 'pending' ? 'Pending' : 'Not verified'}
              </Badge>
            </li>)}
        </ul>
      </Panel>

      <Panel title="Add a phone number" description="Optional, and never shown on your profile." data-id="element-11292" data-mp="1xz6vmqz7hq15">
        <FeatureGate featureKey="phone_verification" data-id="element-11293" data-mp="119g2ow6kcaxg" />
        <div className="mt-4 max-w-sm" data-id="element-11294" data-mp="166zezqjpadmr">
          <Field label="Phone number" htmlFor="settings-phone" data-id="element-11295" data-mp="16812iuhwksld">
            <Input id="settings-phone" value={phone} onChange={event => setPhone(event.target.value)} type="tel" inputMode="tel" autoComplete="tel" data-id="element-11296" data-mp="1a688qveqqbib" />
          </Field>
        </div>
        {message && <p role="status" className="mt-3 text-body-sm text-ink" data-id="element-11297" data-mp="2qkgkrw1vx2fp">
            {message}
          </p>}
        <Button variant="secondary" className="mt-4" disabled={!phone.trim() || verificationCooldown > 0} onClick={() => {
        const outcome = sendVerification('phone', phone.trim());
        setMessage(outcome.ok ? 'A code would be sent once an SMS gateway is configured. Nothing was sent.' : outcome.error ?? null);
      }} data-id="element-11298" data-mp="2p54dy0se5y75">
          {verificationCooldown > 0 ? `Try again in ${verificationCooldown}s` : 'Send a code'}
        </Button>
      </Panel>
    </div>;
}

/* ------------------------------------------------------------------ account */

function AccountSection({
  identity
}: {
  identity: NonNullable<ReturnType<typeof useAuth>['identity']>;
}) {
  const [exported, setExported] = useState(false);
  function exportAccount() {
    const payload = {
      exportedAt: new Date().toISOString(),
      identity: {
        id: identity.id,
        username: identity.username,
        usernameHistory: identity.usernameHistory,
        displayName: identity.displayName,
        email: identity.email,
        countryCode: identity.countryCode,
        institutionId: identity.institutionId,
        campusId: identity.campusId,
        programme: identity.programme,
        level: identity.level,
        interests: identity.interests,
        createdAt: identity.createdAt,
        roles: identity.roles,
        status: identity.status
      },
      consent: identity.consent,
      note: 'Passwords, hashes and salts are deliberately excluded from this export.'
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `campuzon-account-${identity.username}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    setExported(true);
  }
  return <div className="space-y-5" data-id="element-11299" data-mp="1ebh83g8fe5w5">
      <Panel title="What you agreed to" description="The version is recorded. A future version will ask you again rather than assume." data-id="element-11300" data-mp="1ecim9tkqn8i3">
        <dl className="grid gap-4 sm:grid-cols-3" data-id="element-11301" data-mp="14lo0eqbemax6">
          <div data-id="element-11302" data-mp="14i9u56zmdhkg">
            <dt className="text-body-sm text-ink-muted" data-id="element-11303" data-mp="10jawm7g7wn31">Terms of Service</dt>
            <dd className="mt-0.5 text-body font-semibold text-ink" data-id="element-11304" data-mp="10h7u4nxvp1hf">
              {identity.consent?.termsVersion ?? TERMS_VERSION}
            </dd>
          </div>
          <div data-id="element-11305" data-mp="1sbi8kuks3bb3">
            <dt className="text-body-sm text-ink-muted" data-id="element-11306" data-mp="1sb045anypqs3">Privacy Policy</dt>
            <dd className="mt-0.5 text-body font-semibold text-ink" data-id="element-11307" data-mp="1o7ajgwaertcc">
              {identity.consent?.privacyVersion ?? PRIVACY_VERSION}
            </dd>
          </div>
          <div data-id="element-11308" data-mp="ctzqsbk0g2i1">
            <dt className="text-body-sm text-ink-muted" data-id="element-11309" data-mp="cuhv7vgttn11">Accepted</dt>
            <dd className="mt-0.5 text-body font-semibold text-ink" data-id="element-11310" data-mp="7un4brx1wxfz">
              {identity.consent ? new Date(identity.consent.acceptedAt).toLocaleDateString() : 'Not recorded'}
            </dd>
          </div>
        </dl>
      </Panel>

      <Panel title="Take your data with you" data-id="element-11311" data-mp="2oepf5gpm9p3c">
        <p className="max-w-[68ch] text-body-sm text-ink" data-id="element-11312" data-mp="2shvm6qkjzkav">
          Downloads your account record, education details, interests and consent history as a JSON
          file. Your notes export separately from Notes, so you can choose the format.
        </p>
        <Button variant="secondary" className="mt-4" icon={<DownloadIcon className="h-4 w-4" data-id="element-11314" data-mp="375qew6yk9ony" />} onClick={exportAccount} data-id="element-11313" data-mp="32ab3rmrxu6v8">
          Download my account data
        </Button>
        {exported && <p role="status" className="mt-3 text-body-sm text-ok-700" data-id="element-11315" data-mp="3g89emi71vl5z">
            Downloaded. Password hashes are excluded by design.
          </p>}
      </Panel>

      <Panel title="Deactivate or close your account" data-id="element-11316" data-mp="1ltykpvblm0tc">
        <p className="max-w-[68ch] text-body-sm text-ink" data-id="element-11317" data-mp="1lugr3krz5vez">
          Deactivating hides your profile and stops all notifications; you can sign back in to
          restore it. Closing is permanent.
        </p>
        <ul className="mt-4 space-y-2 text-body-sm text-ink-muted" data-id="element-11318" data-mp="xyd765eba1hu">
          <li className="flex items-start gap-2" data-id="element-11319" data-mp="1boyqtwblafgs">
            <ShieldIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-11320" data-mp="1brus9umnbm9z" />
            An open order, an active tenancy or an unresolved dispute must be settled first.
          </li>
          <li className="flex items-start gap-2" data-id="element-11321" data-mp="k0ouhkuee0nv">
            <ShieldIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-11322" data-mp="f0soxh61y167" />
            Ledger entries and audit records are retained. They are anonymised, never deleted,
            because a financial record cannot be erased.
          </li>
          <li className="flex items-start gap-2" data-id="element-11323" data-mp="24ep648oyjneh">
            <ShieldIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-11324" data-mp="24de6ml2jp1t6" />
            Your username is held rather than released, so nobody can take it and appear to be you.
          </li>
        </ul>
        <div className="mt-5 flex flex-wrap gap-3" data-id="element-11325" data-mp="27vblbudblp36">
          <Link to="/support" data-id="element-11326" data-mp="2byq3oroqehjs">
            <Button variant="secondary" data-id="element-11327" data-mp="2bzrq1sb76qgt">Deactivate my account</Button>
          </Link>
          <Link to="/support" data-id="element-11328" data-mp="1xzguibyy0npb">
            <Button variant="ghost" data-id="element-11329" data-mp="1y0jnqqjqn28e">Request account closure</Button>
          </Link>
        </div>
      </Panel>
    </div>;
}